import { Multiplier } from "@/api/multipliers";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/shadcn/tooltip";
import { RotateCcw } from "lucide-react";

interface MultiplierCardProps {
    multiplier: Multiplier;
    editedMultipliers: Map<number, Partial<Multiplier>>;
    onInputChange: (
        id: number,
        field: keyof Pick<Multiplier, "weight" | "maxPoints" | "isActive">,
        value: string | boolean
    ) => void;
    onResetToDefault: (multiplier: Multiplier) => void;
    isLoading: boolean;
}

export function MultiplierCard({
    multiplier,
    editedMultipliers,
    onInputChange,
    onResetToDefault,
    isLoading,
}: MultiplierCardProps) {
    const hasChanges = (): boolean => {
        const edited = editedMultipliers.get(multiplier.id);
        if (!edited) return false;

        return (
            (edited.weight !== undefined && edited.weight !== multiplier.weight) ||
            (edited.maxPoints !== undefined && edited.maxPoints !== multiplier.maxPoints) ||
            (edited.isActive !== undefined && edited.isActive !== multiplier.isActive)
        );
    };

    const getCurrentValue = <T extends keyof Pick<Multiplier, "weight" | "maxPoints" | "isActive">>(
        field: T
    ): Multiplier[T] => {
        const edited = editedMultipliers.get(multiplier.id);
        return edited?.[field] !== undefined ? (edited[field] as Multiplier[T]) : multiplier[field];
    };

    const formatStatName = (name: string): string => {
        return name
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const isChanged = hasChanges();
    const currentWeight = getCurrentValue("weight");
    const currentMaxPoints = getCurrentValue("maxPoints");
    const currentIsActive = getCurrentValue("isActive");

    const isWeightDisabled = (multiplier.weight === null && multiplier.defaultWeight === null) || isLoading;
    const isMaxPointsDisabled = (multiplier.maxPoints === null && multiplier.defaultMaxPoints === null) || isLoading;

    return (
        <div
            className={`relative rounded-xl shadow-sm p-3 transition-all duration-300 flex flex-col h-full ${
                !currentIsActive
                    ? "opacity-40 bg-surface-container-lowest border border-outline-variant/40"
                    : isChanged
                      ? "shadow-lg shadow-general-blue/20 bg-general-blue-background border border-general-blue/70"
                      : "bg-surface-container-lowest border border-outline-variant/40"
            }`}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                    <h4
                        className={`text-sm font-semibold truncate ${
                            !currentIsActive ? "text-on-surface-variant" : "text-on-surface"
                        }`}
                    >
                        {formatStatName(multiplier.statName)}
                    </h4>
                </div>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={() => onResetToDefault(multiplier)}
                            className="cursor-pointer p-1 text-on-surface-variant hover:text-on-surface hover:bg-surface-container rounded transition-colors flex-shrink-0"
                            disabled={isLoading}
                        >
                            <RotateCcw className="w-3 h-3" />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>Reset to default values</TooltipContent>
                </Tooltip>
            </div>

            <p
                className={`text-xs mb-3 line-clamp-2 flex-1 ${!currentIsActive ? "text-on-surface-variant/50" : "text-on-surface-variant"}`}
            >
                {multiplier.description}
            </p>

            <div className="mt-auto space-y-3">
                <div className="flex items-center justify-between">
                    <span
                        className={`text-xs ${!currentIsActive ? "text-on-surface-variant/50" : "text-on-surface-variant"}`}
                    >
                        Active
                    </span>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={currentIsActive}
                            onChange={(e) => onInputChange(multiplier.id, "isActive", e.target.checked)}
                            disabled={isLoading}
                            className="sr-only peer"
                            aria-label={`Toggle ${formatStatName(multiplier.statName)} active state`}
                        />
                        <div
                            className={`w-9 h-5 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-general-blue/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-disabled:opacity-50 peer-disabled:cursor-not-allowed ${
                                currentIsActive
                                    ? "bg-general-blue peer-checked:bg-general-blue"
                                    : "bg-surface-container-high"
                            }`}
                        ></div>
                    </label>
                </div>

                <div className="space-y-2">
                    <div>
                        <label className="flex items-center justify-between text-xs text-on-surface-variant mb-1">
                            <span>Weight</span>
                            {multiplier.defaultWeight !== null && (
                                <span className="text-xs text-on-surface-variant/70">
                                    Def: {multiplier.defaultWeight}
                                </span>
                            )}
                        </label>
                        <input
                            type="number"
                            step="0.1"
                            value={currentWeight ?? ""}
                            onChange={(e) => onInputChange(multiplier.id, "weight", e.target.value)}
                            placeholder={multiplier.weight === null ? "N/A" : "Weight"}
                            disabled={isWeightDisabled}
                            className="w-full px-2 py-1 text-xs bg-surface-container border border-outline-variant rounded text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-general-blue/50 focus:ring-1 focus:ring-general-blue/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed number-input-no-arrows"
                            aria-label="Weight value"
                        />
                    </div>

                    <div>
                        <label className="flex items-center justify-between text-xs text-on-surface-variant mb-1">
                            <span>Max Points</span>
                            {multiplier.defaultMaxPoints !== null && (
                                <span className="text-xs text-on-surface-variant/70">
                                    Def: {multiplier.defaultMaxPoints}
                                </span>
                            )}
                        </label>
                        <input
                            type="number"
                            step="1"
                            value={currentMaxPoints ?? ""}
                            onChange={(e) => onInputChange(multiplier.id, "maxPoints", e.target.value)}
                            placeholder={multiplier.maxPoints === null ? "N/A" : "Max pts"}
                            disabled={isMaxPointsDisabled}
                            className="w-full px-2 py-1 text-xs bg-surface-container border border-outline-variant rounded text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-general-blue/50 focus:ring-1 focus:ring-general-blue/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed number-input-no-arrows"
                            aria-label="Maximum points value"
                        />
                    </div>
                </div>
            </div>

            {isChanged && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-general-blue rounded-full animate-pulse shadow-lg shadow-general-blue/50"></div>
            )}

            {!currentIsActive && (
                <div className="absolute inset-0 bg-surface-container-lowest/20 rounded-md pointer-events-none"></div>
            )}
        </div>
    );
}
